import React, { useRef, useCallback, useEffect, useState } from 'react';
import {
  Card, CardContent, CardHeader, Typography, Grid, Alert,
  Chip, Divider, Box, Button, Tabs, Tab,
} from '@mui/material';
import {
  CheckCircle, Warning, Error, Assessment, TrendingUp, Print,
} from '@mui/icons-material';
import { openFoundationPrintReport } from '../../printReports/appPrintReports';
import { Bar, Line } from 'react-chartjs-2';
import FoundationDiagram from './FoundationDiagram';
import SoilPressureDiagram from './SoilPressureDiagram';
import CapElementPressureMatrix, { capElementPressuresAllZero } from './CapElementPressureMatrix';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  LineElement,
  PointElement,
  LineController,
  Filler,
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  LineElement,
  PointElement,
  LineController,
  Filler
);

function describeSlabAppliedLoad(applied, fallback = {}) {
  if (!applied) {
    const scenario = fallback.load_scenario;
    if (scenario === 'uniform') return 'uniform live load';
    if (scenario === 'concentrated') return `point load ${Number(fallback.concentrated_uls_kN || 0).toFixed(1)} kN ULS`;
    if (scenario === 'line') return 'edge line load';
    return 'self-weight';
  }
  const parts = [`self-weight ${Number(applied.self_weight_kpa).toFixed(2)} kN/m²`];
  if (applied.scenario === 'uniform') {
    parts.push(`imposed ${Number(applied.uniform_live_kpa).toFixed(2)} kN/m² (ULS ${Number(applied.uniform_live_uls_kpa).toFixed(2)})`);
  } else if (applied.scenario === 'concentrated') {
    parts.push(`point ${Number(applied.concentrated_kN).toFixed(1)} kN (ULS ${Number(applied.concentrated_uls_kN).toFixed(1)} kN, ${Number(applied.patch_mm).toFixed(0)} mm patch)`);
  } else if (applied.scenario === 'line') {
    parts.push(`line ${Number(applied.line_kN_per_m).toFixed(2)} kN/m (ULS ${Number(applied.line_uls_kN_per_m).toFixed(2)})`);
  }
  parts.push(`total ULS ${Number(applied.total_applied_uls_kN).toFixed(1)} kN`);
  return parts.join(', ');
}

function slabCaseDistribution(loadCase) {
  const dist = loadCase?.bearing_distribution;
  return Array.isArray(dist) && dist.length > 0 ? dist : null;
}

/** Winkler plots are per scenario. The point-load plot is the corner of the slab. */
function slabWinklerCases(results) {
  const cases = results?.load_cases || [];
  const udl = cases.find((c) => c.load_scenario === 'uniform') || null;
  const point = cases.find((c) => c.load_scenario === 'concentrated' && c.case_type === 'corner')
    || cases.find((c) => c.load_scenario === 'concentrated')
    || null;
  return { udl, point };
}

function SlabAppliedLoad({ applied, gov, formatNumber }) {
  const scenario = applied?.scenario || gov?.load_scenario;
  return (
    <Grid container spacing={2}>
      <Grid item xs={6} md={3}>
        <Typography variant="body2">Self-weight</Typography>
        <Typography variant="h6">
          {formatNumber(applied?.self_weight_kpa, 2)} kN/m²
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ULS {formatNumber(applied?.self_weight_uls_kpa, 2)} kN/m²
        </Typography>
      </Grid>
      {scenario === 'uniform' && (
        <Grid item xs={6} md={3}>
          <Typography variant="body2">Uniform live (characteristic)</Typography>
          <Typography variant="h6">
            {formatNumber(applied?.uniform_live_kpa ?? gov?.load_meta?.uniform_live_kpa, 2)} kN/m²
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ULS {formatNumber(applied?.uniform_live_uls_kpa ?? gov?.uniform_live_uls_kPa, 2)} kN/m²
          </Typography>
        </Grid>
      )}
      {scenario === 'concentrated' && (
        <Grid item xs={6} md={3}>
          <Typography variant="body2">Point load (characteristic)</Typography>
          <Typography variant="h6">
            {formatNumber(applied?.concentrated_kN ?? gov?.load_meta?.concentrated_force_kn, 1)} kN
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ULS {formatNumber(applied?.concentrated_uls_kN ?? gov?.concentrated_uls_kN, 1)} kN
            {' · '}{formatNumber(applied?.patch_mm ?? gov?.load_meta?.patch_mm, 0)} mm patch
          </Typography>
        </Grid>
      )}
      {scenario === 'line' && (
        <Grid item xs={6} md={3}>
          <Typography variant="body2">Line load (characteristic)</Typography>
          <Typography variant="h6">
            {formatNumber(applied?.line_kN_per_m ?? gov?.load_meta?.line_intensity_kn_per_m, 2)} kN/m
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ULS {formatNumber(applied?.line_uls_kN_per_m ?? gov?.line_intensity_uls_kN_per_m, 2)} kN/m
          </Typography>
        </Grid>
      )}
      <Grid item xs={6} md={3}>
        <Typography variant="body2">Total applied (ULS)</Typography>
        <Typography variant="h6">{formatNumber(applied?.total_applied_uls_kN, 1)} kN</Typography>
      </Grid>
    </Grid>
  );
}

function SlabWinklerTabs({ results, winklerTab, onChange, formatNumber }) {
  const { udl, point } = slabWinklerCases(results);
  const selected = winklerTab === 'point' ? point : udl;
  const distribution = slabCaseDistribution(selected);
  const hasAny = Boolean(slabCaseDistribution(udl) || slabCaseDistribution(point));
  if (!hasAny) return null;

  const position = selected?.position;
  const where = position
    ? ` at (${formatNumber(position.x_m, 2)} m, ${formatNumber(position.y_m, 2)} m)`
    : '';
  const caption = !selected
    ? (winklerTab === 'point'
      ? 'No corner point load was analysed.'
      : 'Uniform live load was not analysed.')
    : (winklerTab === 'point'
      ? `${selected.label || 'Corner point load'}${where}. Max soil pressure ${formatNumber(selected.max_soil_pressure_kPa, 1)} kPa. SLS settlement ${formatNumber(selected.deflection_sls_mm, 2)} mm.`
      : `${selected.label || 'Uniform live load'}. Max soil pressure ${formatNumber(selected.max_soil_pressure_kPa, 1)} kPa. SLS settlement ${formatNumber(selected.deflection_sls_mm, 2)} mm.`);

  return (
    <Box sx={{ mt: 2 }}>
      <Tabs
        value={winklerTab}
        onChange={(_, value) => onChange(value)}
        aria-label="Winkler load case"
      >
        <Tab value="udl" label="UDL" />
        <Tab value="point" label="Point load" />
      </Tabs>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 1 }}>
        {caption}
      </Typography>
      {distribution && !capElementPressuresAllZero(distribution) && (
        <CapElementPressureMatrix
          title="Winkler contact pressure"
          subheader={winklerTab === 'point'
            ? 'Corner point load on the slab. Separate from the uniform live load.'
            : 'Uniform live load. Separate from the corner point load.'}
          results={{
            ...results,
            foundation_geometry: { dimensions: results.input_parameters?.dimensions },
            equilibrium: {
              ...(results.equilibrium || {}),
              slab_bearing_pressure_distribution: distribution,
            },
          }}
        />
      )}
    </Box>
  );
}

const FoundationResults = ({ results }) => {
  const capacityChartRef = useRef(null);
  const momentChartRef = useRef(null);
  const resultsRef = useRef(null);
  const [winklerTab, setWinklerTab] = useState('udl');
  useEffect(() => {
    setWinklerTab('udl');
  }, [results]);
  const handlePrintReport = useCallback(() => {
    if (!results) return;
    let chartImageDataUrl = '';
    const inst = capacityChartRef.current;
    if (inst && typeof inst.toBase64Image === 'function') {
      chartImageDataUrl = inst.toBase64Image('image/png', 1);
    }
    let momentChartImageDataUrl = '';
    const momentInst = momentChartRef.current;
    if (momentInst && typeof momentInst.toBase64Image === 'function') {
      momentChartImageDataUrl = momentInst.toBase64Image('image/png', 1);
    }
    const root = resultsRef.current;
    const diagrams = root
      ? Array.from(root.querySelectorAll('[data-print-diagram]')).map((node) => ({
        title: node.getAttribute('data-print-title') || 'Figure',
        html: Array.from(node.querySelectorAll('svg')).map((svg) => svg.outerHTML).join(''),
      })).filter((diagram) => diagram.html)
      : [];
    openFoundationPrintReport(results, { chartImageDataUrl, momentChartImageDataUrl, diagrams });
  }, [results]);

  if (!results) return null;

  if (results.analysis_mode === 'ground_facing_slab') {
    const gov = results.governing_case || {};
    const checks = gov.structural_checks || {};
    const util = results.utilization || {};
    const formatNumber = (value, fractionDigits = 2) => {
      if (value === undefined || value === null) return '0';
      return Number(value).toLocaleString('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).replace(/,/g, ' ');
    };
    return (
      <Box>
        <Card sx={{ mb: 2 }}>
          <CardHeader title="Ground-facing Slab Results" />
          <CardContent>
            <Alert severity={util.status === 'OK' ? 'success' : 'error'} sx={{ mb: 2 }}>
              Overall utilization: {formatNumber(util.max_utilization, 3)} — {util.status}
            </Alert>
            {results.warnings?.map((w, i) => (
              <Alert key={i} severity="warning" sx={{ mb: 1 }}>{w}</Alert>
            ))}
            <Typography variant="subtitle1" gutterBottom>
              Governing: {gov.label || gov.case_type} ({gov.load_scenario || '—'})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Characteristic loads. ULS uses γG and γQ from the design basis. Scenarios are checked separately.
            </Typography>
            {results.mesh_adequacy && gov.load_scenario === 'concentrated' && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Mesh spacing {formatNumber(results.mesh_adequacy.mesh_spacing_m, 3)} m —
                {results.mesh_adequacy.adequate ? ' adequate for patch' : ` only ${formatNumber(results.mesh_adequacy.elements_across_patch, 1)} elements across ${formatNumber(results.mesh_adequacy.patch_side_m * 1000, 0)} mm patch`}
              </Typography>
            )}
            <SlabAppliedLoad applied={gov.applied_load} gov={gov} formatNumber={formatNumber} />
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">Deflection (SLS)</Typography>
                <Typography variant="h6">{formatNumber(gov.deflection_sls_mm, 2)} mm</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">Max soil pressure</Typography>
                <Typography variant="h6">{formatNumber(gov.max_soil_pressure_kPa, 1)} kPa</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">Soil contact</Typography>
                <Typography variant="h6">{formatNumber((gov.contact_ratio || 0) * 100, 0)}%</Typography>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Field slab (bottom steel, sagging)</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {checks.flexure_note || 'Bottom reinforcement for UDL / patch / line loads between supports'}
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6} md={3}><Typography variant="body2">Flexure X util.</Typography><Typography>{formatNumber(checks.flexure_x?.utilization, 3)}</Typography></Grid>
              <Grid item xs={6} md={3}><Typography variant="body2">Flexure Y util.</Typography><Typography>{formatNumber(checks.flexure_y?.utilization, 3)}</Typography></Grid>
              <Grid item xs={6} md={3}><Typography variant="body2">Punching util.</Typography><Typography>{formatNumber(checks.punching?.utilization, 3)}</Typography></Grid>
              <Grid item xs={6} md={3}><Typography variant="body2">Crack width util.</Typography><Typography>{formatNumber(Math.max(checks.crack_x?.utilization || 0, checks.crack_y?.utilization || 0), 3)}</Typography></Grid>
              <Grid item xs={6} md={3}><Typography variant="body2">As,req X (mm²/m)</Typography><Typography>{formatNumber((checks.As_req_x_m2_per_m || 0) * 1e6, 0)}</Typography></Grid>
              <Grid item xs={6} md={3}><Typography variant="body2">As,req Y (mm²/m)</Typography><Typography>{formatNumber((checks.As_req_y_m2_per_m || 0) * 1e6, 0)}</Typography></Grid>
            </Grid>
            {gov.pile_head_checks?.applicable && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Pile head checks (top hogging + punching)</Typography>
                {gov.pile_head_checks.thickness_increase_note && (
                  <Alert severity="warning" sx={{ mb: 1 }}>{gov.pile_head_checks.thickness_increase_note}</Alert>
                )}
                {gov.pile_head_checks.proposed_max_local_thickness_m && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Proposed max local thickness: {formatNumber(gov.pile_head_checks.proposed_max_local_thickness_m, 2)} m
                  </Typography>
                )}
                {(gov.pile_head_checks.piles || []).map((p) => (
                  <Typography key={p.index} variant="body2" sx={{ mb: 0.5 }}>
                    Pile {p.index + 1} ({formatNumber(p.x_m, 2)}, {formatNumber(p.y_m, 2)}): react. {formatNumber(p.reaction_kN, 1)} kN —
                    punch {formatNumber(p.punching?.utilization, 3)}, hogging top {formatNumber(p.hogging_top?.utilization, 3)},
                    As_top {formatNumber(p.hogging_top?.As_req_mm2_per_m, 0)} mm²/m
                    {p.proposed_local_thickness_m ? ` → thicken to ${formatNumber(p.proposed_local_thickness_m, 2)} m` : ''}
                  </Typography>
                ))}
              </>
            )}
            {gov.load_distribution && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2">Load distribution</Typography>
                <Typography variant="body2">
                  Soil: {formatNumber((gov.load_distribution.soil_fraction || 0) * 100, 1)}% ({formatNumber(gov.load_distribution.soil_load_kN, 1)} kN)
                  {' | '}
                  Piles: {formatNumber((gov.load_distribution.pile_fraction || 0) * 100, 1)}% ({formatNumber(gov.load_distribution.pile_load_kN, 1)} kN)
                </Typography>
                {results.soil_support?.note && (
                  <Typography variant="body2" color="text.secondary">{results.soil_support.note}</Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>
        <SlabWinklerTabs
          results={results}
          winklerTab={winklerTab}
          onChange={setWinklerTab}
          formatNumber={formatNumber}
        />
        <Card sx={{ mt: 2 }}>
          <CardHeader title="Load case envelope" />
          <CardContent>
            {(results.load_cases || []).map((lc, idx) => (
              <Box key={`${lc.load_scenario}-${lc.case_type}-${idx}`} sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>{lc.label}</strong> — {describeSlabAppliedLoad(lc.applied_load, lc)}
                  {' · '}util. {formatNumber(lc.max_utilization ?? lc.structural_checks?.max_utilization, 3)}, defl. {formatNumber(lc.deflection_sls_mm, 2)} mm, soil util. {formatNumber(lc.soil_bearing_utilization, 3)}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Box>
    );
  }

  const {
    input_parameters,
    capacities,
    stiffnesses,
    utilization,
    safety_check,
    equilibrium,
    stability_verifications,
    seismic_breakdown,
    seismic_envelope,
    distributed_check,
    fixity_envelope,
    warnings,
    design_basis,
  } = results;

  // Format numbers with space grouping
  const formatNumber = (value, fractionDigits = 0) => {
    if (value === undefined || value === null) return '0';
    return value.toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    }).replace(/,/g, ' ');
  };

  const fixityMode = input_parameters?.pile_head_fixity_mode || 'override';
  const fixityValue = Number(input_parameters?.pile_head_fixity ?? 0);
  const fixityPreset = input_parameters?.pile_head_fixity_preset;
  const fixityComputed = input_parameters?.pile_head_fixity_computed || {};
  const fixityAudit = input_parameters?.pile_head_fixity_audit;
  const fixitySummary = (() => {
    if (fixityMode === 'envelope') {
      return 'Envelope of pinned (0.00) and fixed (1.00); governing bound varies by metric';
    }
    if (fixityMode === 'preset') {
      const lbl = fixityPreset ? `${fixityPreset} preset` : 'preset';
      return `${fixityValue.toFixed(2)} (${lbl})`;
    }
    if (fixityMode === 'computed') {
      return `${fixityValue.toFixed(2)} (Computed: embed ${formatNumber(fixityComputed.cap_embedment_m || 0, 2)} m, ${fixityComputed.top_rebar_development || 'none'} rebar, cap ${formatNumber(fixityComputed.cap_thickness_m || 0, 2)} m)`;
    }
    return `${fixityValue.toFixed(2)} (Advanced override)`;
  })();

  const capacityData = {
    labels: ['Compression', 'Tension', 'Lateral X', 'Lateral Y'],
    datasets: [{
      label: 'Capacity (kN)',
      data: [
        capacities.compression,
        capacities.tension,
        capacities.lateral_x,
        capacities.lateral_y
      ],
      backgroundColor: '#2196f3',
    }]
  };

  const capacityOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'Design resistances (kN)' },
    },
  };

  return (
    <div ref={resultsRef}>
      <Typography variant="h4" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Assessment />
        Analysis Results
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<Print />} onClick={handlePrintReport}>
          Print report (PDF)
        </Button>
      </Box>

      {/* Safety Status */}
      <Alert
        severity={safety_check ? "success" : "error"}
        sx={{ mb: 2 }}
        icon={safety_check ? <CheckCircle /> : <Error />}
      >
        {safety_check
          ? "Foundation design PASSES all safety requirements"
          : "Foundation design FAILS safety requirements - redesign needed"
        }
      </Alert>

      {design_basis && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Design basis (EC7 DA2 — Finnish NA)" titleTypographyProps={{ variant: 'h6' }} />
          <CardContent>
            <Typography variant="body2" component="div">
              <strong>Framework:</strong> {design_basis.code_editions}<br />
              <strong>Design approach:</strong> {design_basis.design_approach}<br />
              <strong>Bearing γ<sub>Rv</sub>:</strong> {design_basis.gamma_Rv_bearing}<br />
              <strong>Pile γ<sub>b</sub> / γ<sub>s</sub>:</strong> {design_basis.gamma_b} / {design_basis.gamma_s}<br />
              <strong>ξ (base / shaft):</strong> {design_basis.xi_base} / {design_basis.xi_shaft}<br />
              {design_basis.q_ult_primary_kPa != null && (
                <>
                  <strong>q<sub>ult</sub> (primary layer):</strong> {formatNumber(design_basis.q_ult_primary_kPa, 0)} kPa<br />
                </>
              )}
              <em>{design_basis.note}</em>
            </Typography>
          </CardContent>
        </Card>
      )}

      {seismic_breakdown && (
        <Card sx={{ mb: 2, borderLeft: 4, borderColor: 'info.main' }}>
          <CardHeader title="Eurocode 8 — seismic" titleTypographyProps={{ variant: 'h6' }} />
          <CardContent sx={{ pt: 0 }}>
            <Grid container spacing={2}>
              {seismic_breakdown.base_shear && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Base shear / spectrum</Typography>
                  <Typography variant="body2">
                    F<sub>b</sub> = {formatNumber(seismic_breakdown.base_shear.F_b_kN, 1)} kN · T<sub>1</sub> ={' '}
                    {formatNumber(seismic_breakdown.base_shear.T1_s, 2)} s · S<sub>d</sub>(T<sub>1</sub>) ={' '}
                    {formatNumber(seismic_breakdown.base_shear.Sd_T1_ms2, 3)} m/s²
                  </Typography>
                  <Typography variant="body2">
                    Ground type: {seismic_breakdown.base_shear.ground_type} · q = {formatNumber(seismic_breakdown.base_shear.q, 2)}
                  </Typography>
                </Grid>
              )}
              {seismic_breakdown.capacity_design_factor != null && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Capacity design</Typography>
                  <Typography variant="body2">
                    γ<sub>Rd</sub>·Ω = {formatNumber(seismic_breakdown.capacity_design_factor, 2)}
                  </Typography>
                </Grid>
              )}
              {seismic_breakdown.liquefaction && !seismic_breakdown.liquefaction.skipped && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Liquefaction (screening)</Typography>
                  {seismic_breakdown.liquefaction.any_liquefied ? (
                    <Alert severity="warning">One or more layers flagged as liquefied (shaft resistance reduced).</Alert>
                  ) : (
                    <Typography variant="body2">No liquefied layers (FS ≥ 1.25 or not applicable).</Typography>
                  )}
                </Grid>
              )}
              {seismic_breakdown.kinematic && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Kinematic bending</Typography>
                  {seismic_breakdown.kinematic.skipped ? (
                    <Typography variant="body2" color="text.secondary">
                      {seismic_breakdown.kinematic.reason || 'Not triggered (EC8-5 §5.4.2 conditions).'}
                    </Typography>
                  ) : (
                    <Typography variant="caption" component="div">
                      {(seismic_breakdown.kinematic.interfaces || []).map((row, i) => (
                        <span key={i}>
                          z = {formatNumber(row.depth_m, 1)} m: M<sub>kin</sub> ≈ {formatNumber(row.M_kin_kNm, 1)} kNm ·{' '}
                        </span>
                      ))}
                    </Typography>
                  )}
                </Grid>
              )}
              {seismic_breakdown.mononobe_okabe && seismic_breakdown.mononobe_okabe.details && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Mononobe–Okabe (embedded cap)</Typography>
                  <Typography variant="body2">
                    ΔP<sub>AE</sub> ≈ {formatNumber(seismic_breakdown.mononobe_okabe.Delta_P_AE_kN, 1)} kN · K<sub>AE</sub> ={' '}
                    {formatNumber(seismic_breakdown.mononobe_okabe.details.K_AE, 3)}
                  </Typography>
                </Grid>
              )}
              {seismic_envelope && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Directional envelope: governing combo {seismic_envelope.governing_combo_index + 1} of{' '}
                    {seismic_envelope.combos_evaluated} (±30% rule)
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {distributed_check && distributed_check.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Distributed pile check failed: {String(distributed_check.error)}
        </Alert>
      )}

      {Array.isArray(warnings) && warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warnings.join(' ')}
        </Alert>
      )}

      {distributed_check && !distributed_check.error && distributed_check.z_m && (
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title="Distributed pile check (experimental)"
            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
          />
          <CardContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Max N–M utilization (along shaft):{' '}
              <strong>{((distributed_check.max_utilization || 0) * 100).toFixed(1)}%</strong>
              {' · '}
              Critical depth: <strong>{formatNumber(distributed_check.critical_depth_m, 2)} m</strong>
              {' · '}
              Head-level max UR: <strong>{((utilization?.max_utilization || 0) * 100).toFixed(1)}%</strong>
              {' (includes distributed when enabled)'}
            </Typography>
            <Box sx={{ height: 260, position: 'relative' }}>
              <Line
                ref={momentChartRef}
                data={{
                  labels: (distributed_check.z_m || []).map((z) => formatNumber(z, 2)),
                  datasets: [
                    {
                      label: 'M static (kNm)',
                      data: distributed_check.M_static_kNm || [],
                      borderColor: 'rgb(33, 150, 243)',
                      backgroundColor: 'rgba(33, 150, 243, 0.1)',
                      tension: 0.2,
                      fill: false,
                    },
                    {
                      label: 'M with u_ff (kNm)',
                      data: distributed_check.M_with_free_field_kNm || [],
                      borderColor: 'rgb(211, 47, 47)',
                      backgroundColor: 'rgba(211, 47, 47, 0.05)',
                      tension: 0.2,
                      fill: false,
                    },
                    {
                      label: 'M kin increment (kNm)',
                      data: distributed_check.M_kin_increment_kNm || [],
                      borderColor: 'rgb(46, 125, 50)',
                      borderDash: [6, 4],
                      tension: 0.2,
                      fill: false,
                    },
                    ...(Array.isArray(distributed_check.bounds) && distributed_check.bounds.length === 2 ? [
                      {
                        label: 'M with u_ff bound (fixity=0.0)',
                        data: distributed_check.bounds[0]?.M_with_free_field_kNm || [],
                        borderColor: 'rgba(255, 152, 0, 0.9)',
                        borderDash: [3, 3],
                        tension: 0.2,
                        fill: false,
                      },
                      {
                        label: 'M with u_ff bound (fixity=1.0)',
                        data: distributed_check.bounds[1]?.M_with_free_field_kNm || [],
                        borderColor: 'rgba(156, 39, 176, 0.9)',
                        borderDash: [3, 3],
                        tension: 0.2,
                        fill: false,
                      },
                    ] : []),
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'top' },
                    title: { display: true, text: 'Bending moment vs depth along pile' },
                  },
                  scales: {
                    x: {
                      title: { display: true, text: 'Depth z (m) — node labels' },
                      ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 12 },
                    },
                    y: { title: { display: true, text: 'M (kNm)' } },
                  },
                }}
              />
            </Box>
            {fixityMode === 'envelope' && fixity_envelope?.governing_per_metric && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Governing bound by metric:
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {Object.entries(fixity_envelope.governing_per_metric)
                    .slice(0, 8)
                    .map(([k, v]) => `${k}: fixity=${v === 1 ? '1.0' : '0.0'}`)
                    .join(' | ')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', letterSpacing: 1.2, mb: 1 }}>
        A. Eurocode 7 — limit-state compliance
      </Typography>

      <Grid container spacing={3}>
        {/* Eurocode 7 Compliance — first for review workflow */}
        <Grid item xs={12}>
          <Card sx={{ borderLeft: 6, borderColor: safety_check ? 'success.main' : 'error.main', bgcolor: safety_check ? 'success.50' : 'error.50' }}>
            <CardHeader 
              title="Eurocode 7 design compliance" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
              avatar={safety_check ? <CheckCircle color="success" /> : <Error color="error" />}
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg={3}>
                  <Alert severity={utilization.compression <= 1.0 ? "success" : "error"} sx={{ height: '100%', border: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>
                      ULS COMPRESSION (§6.5.2)
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      F<sub>Ed</sub> ≤ R<sub>cd</sub>
                    </Typography>
                    <Typography variant="body2">
                      {formatNumber(Math.abs(input_parameters.loads.N))} kN ≤ {formatNumber(capacities.compression || 0)} kN
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                      {(utilization.compression * 100 || 0).toFixed(1)}% UR
                    </Typography>
                  </Alert>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                  <Alert severity={results.stability_verifications.tension.status === 'PASS' ? "success" : "error"} sx={{ height: '100%', border: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>
                      ULS TENSION (§6.5.3)
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      F<sub>t,Ed</sub> ≤ R<sub>td</sub>
                    </Typography>
                    <Typography variant="body2">
                      {formatNumber(results.stability_verifications.tension.load || 0)} kN ≤ {formatNumber(results.stability_verifications.tension.capacity || 0)} kN
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                      {results.stability_verifications.tension.capacity > 0 
                        ? ((results.stability_verifications.tension.load / results.stability_verifications.tension.capacity) * 100).toFixed(1) 
                        : (results.stability_verifications.tension.load > 0 ? "100.0" : "0.0")}% UR
                    </Typography>
                  </Alert>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                  <Alert severity={results.stability_verifications.sliding.status === 'PASS' ? "success" : "error"} sx={{ height: '100%', border: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>
                      SLIDING STABILITY (§6.5.3)
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      H<sub>Ed</sub> ≤ R<sub>hd</sub>
                    </Typography>
                    <Typography variant="body2">
                      {formatNumber(results.stability_verifications.sliding.driving_force)} kN ≤ {formatNumber(results.stability_verifications.sliding.resisting_force)} kN
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                      {((results.stability_verifications.sliding.driving_force / results.stability_verifications.sliding.resisting_force) * 100).toFixed(1)}% UR
                    </Typography>
                  </Alert>
                </Grid>

                <Grid item xs={12} md={6} lg={3}>
                  <Alert severity={results.stability_verifications.lateral_x.status === 'PASS' && results.stability_verifications.lateral_y.status === 'PASS' ? "success" : "error"} sx={{ height: '100%', border: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 0.5 }}>
                      LATERAL LOAD (§6.5.4)
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      V<sub>Ed</sub> ≤ V<sub>Rd</sub>
                    </Typography>
                    <Typography variant="body2">
                      {formatNumber(Math.max(results.stability_verifications.lateral_x.load, results.stability_verifications.lateral_y.load))} kN ≤ {formatNumber(Math.min(results.stability_verifications.lateral_x.capacity, results.stability_verifications.lateral_y.capacity))} kN
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                      {(Math.max(utilization.lateral_x || 0, utilization.lateral_y || 0) * 100).toFixed(1)}% UR
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', letterSpacing: 1.2, mb: 1, mt: 1 }}>
            B. Model and loads
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Analysis summary" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Foundation: {input_parameters.foundation_type} · {input_parameters.num_piles} piles · {input_parameters.pile_code}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Length: {input_parameters.pile_length} m {input_parameters.actual_physical_length && input_parameters.actual_physical_length !== input_parameters.pile_length ? `(clipped to ${input_parameters.actual_physical_length.toFixed(2)} m)` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Head fixity: {fixitySummary}
                  </Typography>
                  {fixityAudit && (
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {fixityAudit}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Concrete: {input_parameters.concrete_grade}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Soil layers: {input_parameters.soil_layers?.map((layer) =>
                      `${layer.name} (${layer.thickness}m)`
                    ).join(', ')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Vertical load: {formatNumber(input_parameters.loads.N)} kN
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cap self-weight: {formatNumber(capacities.cap_weight || 0)} kN
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Horizontal load: Vx {formatNumber(input_parameters.loads.Vx || 0)} kN · Vy {formatNumber(input_parameters.loads.Vy || 0)} kN
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Moment: Mx {formatNumber(input_parameters.loads.Mx || 0)} kNm · My {formatNumber(input_parameters.loads.My || 0)} kNm · Mz {formatNumber(input_parameters.loads.Mz || input_parameters.loads.moment_z || 0)} kNm
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Capacity breakdown + design resistances (bar) */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Capacity breakdown" subheader="How compression and lateral resistance are made up" />
            <CardContent>
              <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Compression resistance:</strong>
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    Soil Bearing (at yield):
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {formatNumber(capacities.soil_bearing_yield || 0)} kN
                  </Typography>
                </Grid>
                {input_parameters.foundation_type !== 'spread_footing' && (
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Pile Group:
                    </Typography>
                    <Typography variant="h6" color="info.main">
                      {formatNumber(capacities.pile_compression_group || 0)} kN
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* Detailed Pile Capacity Breakdown (if available) */}
              {stability_verifications.torsion && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Torsional Analysis (6-DOF):</strong>
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2">Applied Torsion (Mz):</Typography>
                      <Typography variant="h6">{(stability_verifications.torsion.moment_z_kNm || 0).toFixed(1)} kNm</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">Rotation (θz):</Typography>
                      <Typography variant="h6">{(stability_verifications.torsion.rotation_z_deg || 0).toFixed(4)}°</Typography>
                    </Grid>
                  </Grid>
                </>
              )}

              {input_parameters.foundation_type !== 'spread_footing' && capacities.pile_compression_group && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    <strong>Individual Pile Capacity Details:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Single Pile Geotechnical Capacity: ~{capacities.single_pile_geotech ? capacities.single_pile_geotech.toFixed(0) : (capacities.pile_compression_group / (input_parameters.num_piles || 5)).toFixed(0)} kN<br/>
                    Group Efficiency Factor: {capacities.pile_group_efficiency ? capacities.pile_group_efficiency.toFixed(3) : 'N/A'}<br/>
                    Buckling Capacity: {capacities.single_pile_buckling && capacities.single_pile_buckling < 999999 ? `${capacities.single_pile_buckling.toFixed(0)} kN` : 'Not limiting (high capacity)'}<br/>
                    Max pile utilization: {(utilization.max_pile_utilization * 100 || 0).toFixed(1)}%<br/>
                    Structural Capacity: Limited by concrete/reinforcement strength (from manufacturer data)
                  </Typography>
                </>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                <strong>Lateral resistance components:</strong>
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    Piles (R<sub>h,geotech</sub>): <strong>{formatNumber(results.stability_verifications.sliding?.Rh_geotech || 0)} kN</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    Cap side bearing (R<sub>passive</sub>): <strong>{formatNumber(results.stability_verifications.sliding?.passive_pressure || 0)} kN</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    Cap underside friction (R<sub>sliding</sub>): <strong>{formatNumber(results.stability_verifications.sliding?.base_friction || 0)} kN</strong>
                    {results.input_parameters?.soil_support_active === false && (
                      <> — 0 kN because the soffit is not in bearing contact. Passive pressure on the vertical face still applies.</>
                    )}
                  </Typography>
                </Grid>
              </Grid>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ height: { xs: 300, md: 380 }, position: 'relative', minHeight: 260 }}>
                    <Bar ref={capacityChartRef} data={capacityData} options={capacityOptions} />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', letterSpacing: 1.2, mb: 1, mt: 0.5 }}>
            C. Foundation geometry &amp; soil pressure
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <div data-print-diagram data-print-title="Foundation layout">
            <FoundationDiagram results={results} />
          </div>
        </Grid>

        {!capElementPressuresAllZero(results?.equilibrium?.slab_bearing_pressure_distribution) && (
          <Grid item xs={12}>
            <div data-print-diagram data-print-title="Cap element pressures">
              <CapElementPressureMatrix results={results} />
            </div>
          </Grid>
        )}

        <Grid item xs={12}>
          <div data-print-diagram data-print-title="Soil pressure">
            <SoilPressureDiagram results={results} />
          </div>
        </Grid>

        {/* Detailed Calculations */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Detailed Calculations" />
            <CardContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                Technical verification calculations and detailed analysis results:
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Settlement Assessment
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>Max Settlement:</strong> {formatNumber(equilibrium.max_settlement_mm || 0, 2)} mm<br/>
                    <strong>Min Settlement:</strong> {formatNumber(equilibrium.min_settlement_mm || 0, 2)} mm<br/>
                    <strong>Avg Settlement:</strong> {formatNumber(equilibrium.avg_settlement_mm || 0, 2)} mm<br/>
                    <strong>Differential Settlement:</strong> {formatNumber(((equilibrium.max_settlement_mm || 0) - (equilibrium.min_settlement_mm || 0)), 2)} mm<br/>
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Stiffness Analysis
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>Soil stiffness (k<sub>geo</sub>):</strong> {formatNumber(stiffnesses?.k_geo || 0)} kN/m<br/>
                    <strong>Shaft Friction Stiffness (k<sub>s</sub>):</strong> {formatNumber(stiffnesses?.k_shaft || 0)} kN/m (per pile)<br/>
                    <strong>Tip Elastic Stiffness (k<sub>b</sub>):</strong> {formatNumber(stiffnesses?.k_tip || 0)} kN/m (per pile)<br/>
                    <strong>Pile Structural Stiffness (EA/L):</strong> {formatNumber(stiffnesses?.k_pile_structural || 0)} kN/m (per pile)<br/>
                    <strong>Combined Vertical Pile Stiffness (k<sub>tot</sub>):</strong> {formatNumber(stiffnesses?.k_pile_vertical || 0)} kN/m (per pile)<br/>
                    <Divider sx={{ my: 1 }} />
                    <strong>Foundation Slab Subgrade Stiffness:</strong> {formatNumber(stiffnesses?.k_cap || 0)} kN/m (combined)<br/>
                    <Divider sx={{ my: 1 }} />
                    <strong>Lateral Stiffnesses:</strong><br/>
                    • Pile Lateral: {formatNumber(stiffnesses?.k_lateral_pile || 0)} kN/m (per pile)<br/>
                    • Slab Base Friction: {formatNumber(stiffnesses?.k_lateral_slab_friction || 0)} kN/m (effective lateral stiffness)<br/>
                    • Slab Passive Pressure: {formatNumber(stiffnesses?.k_lateral_slab_passive || 0)} kN/m (effective lateral stiffness)<br/>
                    <strong>Rotational Stiffness (k<sub>rot</sub>):</strong> {formatNumber(stiffnesses?.k_rotational || 0)} kNm/rad (combined)<br/>
                  </Typography>
                </Grid>

                {/* Pile Group Analysis */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Pile Group Equilibrium Analysis
                  </Typography>
                  {results.equilibrium && !results.equilibrium.error ? (
                    <div>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Pile group equilibrium solved successfully:
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <Alert severity="info" icon={<TrendingUp />}>
                            <Typography variant="body2">
                              <strong>Equilibrium Status:</strong><br/>
                              {results.equilibrium.status === "converged" ? "Converged" :
                               results.equilibrium.status === "failed_convergence" ? "Failed to Converge" :
                               results.equilibrium.status === "unstable_liftoff" ? "Unstable (Pile Liftoff)" :
                               results.equilibrium.status === "unstable_singular" ? "Unstable (Singular Matrix)" :
                               `Status: ${results.equilibrium.status}`}
                            </Typography>
                          </Alert>
                        </Grid>
                        {results.equilibrium.pile_loads && (
                          <Grid item xs={12} md={9}>
                            <Typography variant="body2" color="text.secondary">
                              Individual pile loads calculated for group equilibrium
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </div>
                  ) : (
                    <Alert severity="warning">
                      <Typography variant="body2">
                        <strong>Equilibrium Analysis:</strong><br/>
                        {results.equilibrium?.error || "Analysis not available with current calculation engine"}
                      </Typography>
                    </Alert>
                  )}
                </Grid>

                {/* EN 1997-1 Stability Verifications */}
                {results.stability_verifications && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Load share, overturning, and bearing
                    </Typography>

                    {/* Load Distribution */}
                    {results.stability_verifications.load_distribution && (
                      <Grid item xs={12} sx={{ mb: 2 }}>
                        <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                          <CardContent>
                            <Typography variant="h6" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TrendingUp /> Load Distribution Analysis
                            </Typography>
                            
                            <Grid container spacing={2}>
                              {/* Vertical Load Distribution */}
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom borderBottom={1} borderColor="divider">
                                  Vertical Load
                                </Typography>
                                <Typography variant="body2" component="div">
                                  <strong>Total Vertical Load:</strong> {formatNumber(results.stability_verifications.load_distribution.total_load)} kN<br/>
                                  <strong>Pile Load:</strong> {formatNumber(results.stability_verifications.load_distribution.pile_load)} kN
                                  ({(results.stability_verifications.load_distribution.pile_fraction * 100).toFixed(1)}%)<br/>
                                  <strong>Soil Bearing:</strong> {formatNumber(results.stability_verifications.load_distribution.soil_load)} kN
                                  ({(results.stability_verifications.load_distribution.soil_fraction * 100).toFixed(1)}%)<br/>
                                  {results.stability_verifications.load_distribution.soil_support_note && (
                                    <><em>{results.stability_verifications.load_distribution.soil_support_note}</em><br/></>
                                  )}
                                </Typography>
                              </Grid>

                              {/* Lateral Load Distribution */}
                              {results.stability_verifications.load_distribution.shear_x && (
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom borderBottom={1} borderColor="divider">
                                    Horizontal Load (Shear)
                                  </Typography>
                                  <Typography variant="body2" component="div">
                                    <strong>Total Shear Load X:</strong> {formatNumber(results.stability_verifications.load_distribution.shear_x.total_applied)} kN<br/>
                                    <strong>Total Shear Load Y:</strong> {formatNumber(results.stability_verifications.load_distribution.shear_y.total_applied)} kN<br/>
                                    <Divider sx={{ my: 0.5 }} />
                                    <strong>Shear Transfer Breakdown (X | Y):</strong><br/>
                                    • Piles: {formatNumber(results.stability_verifications.load_distribution.shear_x.pile)} | {formatNumber(results.stability_verifications.load_distribution.shear_y.pile)} kN<br/>
                                    • Soil Friction: {formatNumber(results.stability_verifications.load_distribution.shear_x.soil_friction)} | {formatNumber(results.stability_verifications.load_distribution.shear_y.soil_friction)} kN<br/>
                                    • Passive Pressure: {formatNumber(results.stability_verifications.load_distribution.shear_x.soil_passive)} | {formatNumber(results.stability_verifications.load_distribution.shear_y.soil_passive)} kN<br/>
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>

                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                              * Load distribution based on relative stiffness and nonlinear soil-structure interaction.
                              Net torsion effects are accounted for in the global summation.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}

                    <Grid container spacing={3}>
                      {/* Overturning Stability */}
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" color="secondary" gutterBottom>
                              Overturning Stability (EQU)
                            </Typography>
                            <Typography variant="body2" component="div">
                              <strong>Resisting Moment:</strong> {results.stability_verifications.overturning.resisting_moment} kNm<br/>
                              <strong>Driving Moment:</strong> {results.stability_verifications.overturning.driving_moment} kNm<br/>
                              <strong>Utilization (η):</strong> {results.stability_verifications.overturning.utilization}<br/>
                              <strong>Requirement:</strong> η ≤ 1.0 (γ<sub>M</sub> = 1.5 on resistance)
                            </Typography>
                            <Chip
                              label={results.stability_verifications.overturning.status}
                              color={results.stability_verifications.overturning.status === 'PASS' ? 'success' : 'error'}
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Soil Bearing Check */}
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="h6" color="secondary" gutterBottom>
                              Soil Bearing (SSI)
                            </Typography>
                            <Typography variant="body2" component="div">
                              <strong>Average Pressure:</strong> {results.stability_verifications.bearing.average_pressure} kPa<br/>
                              <strong>Max Pressure:</strong> {results.stability_verifications.bearing.max_pressure} kPa<br/>
                              <strong>Design pressure q<sub>d</sub> (q<sub>ult</sub>/γ<sub>Rv</sub>):</strong> {results.stability_verifications.bearing.allowable_pressure} kPa<br/>
                              {results.stability_verifications.bearing.q_ult_kPa != null && (
                                <>
                                  <strong>q<sub>ult</sub>:</strong> {results.stability_verifications.bearing.q_ult_kPa} kPa<br/>
                                </>
                              )}
                              {results.stability_verifications.bearing.utilization_ec7 != null && (
                                <>
                                  <strong>Bearing utilization (EC7):</strong>{' '}
                                  {(results.stability_verifications.bearing.utilization_ec7 * 100).toFixed(1)}%<br/>
                                </>
                              )}
                              <strong>Eccentricity:</strong> {results.stability_verifications.bearing.eccentricity ?? '—'} m<br/>
                              <strong>Note:</strong>{' '}
                              {results.input_parameters?.soil_support_active === false
                                ? 'Soil bearing under this piled foundation is off (EN 1997-1 / RIL 254): 0 kN (0%). Vertical-face passive pressure is still used.'
                                : 'Soil springs yield at the strain-compatible pressure; piles carry the rest.'}
                              <br/>
                            </Typography>
                            <Chip
                              label={results.stability_verifications.bearing.status}
                              color={results.stability_verifications.bearing.status === 'ANALYZED' ? 'info' : (results.stability_verifications.bearing.status === 'PASS' ? 'success' : 'error')}
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Reinforcement Details */}
                      <Grid item xs={12}>
                        <Card variant="outlined" sx={{ mb: 2, bgcolor: 'primary.50' }}>
                          <CardContent>
                            <Typography variant="subtitle1" color="primary" gutterBottom>
                              Slab Reinforcement Verification
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={6} md={3}>
                                <Typography variant="body2"><strong>Bot Rebar (As,bot):</strong></Typography>
                                <Typography variant="body1">{(results.stability_verifications.reinforcement?.as_bot_mm2 || 0).toFixed(0)} mm²</Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="body2"><strong>Top Rebar (As,top):</strong></Typography>
                                <Typography variant="body1">{(results.stability_verifications.reinforcement?.as_top_mm2 || 0).toFixed(0)} mm²</Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="body2"><strong>Min Rebar (As,min):</strong></Typography>
                                <Typography variant="body1">{(results.stability_verifications.reinforcement?.as_min_mm2 || 0).toFixed(0)} mm²</Typography>
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <Typography variant="body2"><strong>Hogging Tension:</strong></Typography>
                                <Typography variant="body1">{(results.stability_verifications.reinforcement?.tension_top_kN || 0).toFixed(1)} kN</Typography>
                              </Grid>
                            </Grid>
                            {results.stability_verifications.reinforcement?.top_rebar_warning && (
                              <Alert severity="warning" sx={{ mt: 2 }}>
                                <strong>⚠️ Missing Top Reinforcement:</strong> Hogging moments are substantial ({results.stability_verifications.reinforcement?.tension_top_kN} kN tension). 
                                Recommend at least As,min = {results.stability_verifications.reinforcement?.as_min_mm2} mm² at top face.
                              </Alert>
                            )}
                            {results.stability_verifications.reinforcement?.ec2_pile_cap && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                  EN 1992 slab checks (pile cap)
                                </Typography>
                                <Typography variant="body2" component="div">
                                  {results.stability_verifications.reinforcement.ec2_pile_cap.column_punching?.skipped ? (
                                    <>
                                      <strong>Column punching:</strong> not evaluated — enable &quot;Check column punching&quot; in Project Configuration and enter column plan dimensions.
                                      <br/>
                                    </>
                                  ) : (
                                    <>
                                      <strong>Column punching utilization:</strong>{' '}
                                      {(results.stability_verifications.reinforcement.ec2_pile_cap.column_punching.utilization * 100).toFixed(1)}%
                                      {' '}(V<sub>Ed</sub> = {Number(results.stability_verifications.reinforcement.ec2_pile_cap.column_punching.V_Ed_kN).toFixed(1)} kN,
                                      V<sub>Rd,c</sub> = {Number(results.stability_verifications.reinforcement.ec2_pile_cap.column_punching.V_Rd_kN).toFixed(1)} kN)
                                      <br/>
                                    </>
                                  )}
                                  <strong>Pile punching (max):</strong>{' '}
                                  {(results.stability_verifications.reinforcement.ec2_pile_cap.pile_punching.max_utilization * 100).toFixed(1)}%
                                  <br/>
                                  <strong>Hogging bending utilization:</strong>{' '}
                                  {(results.stability_verifications.reinforcement.ec2_pile_cap.bending_hogging.utilization * 100).toFixed(1)}%
                                  {results.stability_verifications.reinforcement.ec2_pile_cap.strut_and_tie?.applicable && (
                                    <>
                                      <br />
                                      <strong>Strut-and-tie (a/d &lt; 2):</strong>{' '}
                                      {(results.stability_verifications.reinforcement.ec2_pile_cap.strut_and_tie.strut_utilization * 100).toFixed(1)}%
                                    </>
                                  )}
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Pile Tension Warning */}
                      {results.stability_verifications.pile_tension_warning && (
                        <Grid item xs={12}>
                          <Alert severity="warning" icon={<Warning />}>
                            <Typography variant="body2">
                              <strong>⚠️ Pile Tension Warning:</strong> One or more piles are calculated to be in tension.
                              This is generally undesirable in pile foundation design and may indicate poor load distribution or foundation instability.
                            </Typography>
                          </Alert>
                        </Grid>
                      )}

                      {/* Soil Parameters Used */}
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          <strong>Soil Parameters Used:</strong> c<sub>u</sub> = {results.stability_verifications.soil_parameters.cu} kPa,
                          φ = {results.stability_verifications.soil_parameters.phi}°
                          ({results.stability_verifications.soil_parameters.type} soil)
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default FoundationResults;
